import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import VerifiedBadge from '../../components/UI/VerifiedBadge/VerifiedBadge';
import ArtworkCard from '../../components/ArtworkCard/ArtworkCard';
import './SearchPage.css';

const SearchPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [query, setQuery] = useState(searchParams.get('q') || '');

    const [results, setResults] = useState({ artists: [], artworks: [] });
    const [isDiscovery, setIsDiscovery] = useState(false);
    const [loading, setLoading] = useState(false);

    const performSearch = useCallback(async (searchQuery) => {
        setLoading(true);
        try {
            const resp = await api.get(`/search/global`, { params: { q: searchQuery } });
            setResults(resp.data);
            setIsDiscovery(resp.data.isDiscovery || false);
        } catch (err) {
            console.error("Error searching", err);
            setResults({ artists: [], artworks: [] });
        } finally {
            setLoading(false);
        }
    }, []);

    // Load Explore Feed (Initial) or Search
    useEffect(() => {
        const q = searchParams.get('q') || '';
        const delayDebounceFn = setTimeout(() => {
            performSearch(q);
        }, q ? 500 : 0); // Immediate if empty

        return () => clearTimeout(delayDebounceFn);
    }, [searchParams, performSearch]);

    const handleSearchInput = (value) => {
        setQuery(value);
        // Update URL, which triggers the useEffect above
        if (value) {
            setSearchParams({ q: value });
        } else {
            setSearchParams({});
        }
    };

    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        performSearch(query);
    };

    /* ToggleTab and executeSearch removed */

    return (
        <div className="search-page-container">
            <div className="search-header minimalist">
                <form onSubmit={handleSearchSubmit} className="search-bar simple">
                    <div className="input-wrapper">
                        <span className="search-icon">🔍</span>
                        <input
                            value={query}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            placeholder={t('search_placeholder')}
                            autoFocus
                        />
                    </div>
                </form>
            </div>

            <div className="search-results minimalist">
                {loading ? (
                    <div className="loading-state">{t('loading')}</div>
                ) : (
                    <>
                        {isDiscovery && query && (
                            <div className="discovery-header">
                                <p>{t('no_exact_results', 'Não encontramos resultados exatos para sua busca, mas veja o que separamos para você:')}</p>
                            </div>
                        )}

                        <div className="results-container">
                            {results.artists && results.artists.length > 0 && (
                                <div className="section-results">
                                    <h3 className="section-title">{t('artists')}</h3>
                                    <div className="artists-list">
                                        {results.artists.map(artist => (
                                            <div key={artist.id} className="artist-item-row" onClick={() => navigate(`/profile/${artist.user?.username}`)}>
                                                {artist.profilePicture ? (
                                                    <img src={artist.profilePicture} alt={artist.user?.username} />
                                                ) : (
                                                    <div className="artist-avatar-fallback">
                                                        {(artist.user?.username || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="artist-info">
                                                    <h4>{artist.user?.username}</h4>
                                                    <span>
                                                        {artist.artistName}
                                                        {artist.isVerified && <VerifiedBadge size={14} />}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {results.artworks && results.artworks.length > 0 && (
                                <div className="section-results">
                                    <h3 className="section-title">{!query || isDiscovery ? t('explore', 'Destaques') : t('artworks')}</h3>
                                    <div className="artworks-grid three-col">
                                        {results.artworks.map(artwork => (
                                            <ArtworkCard
                                                key={artwork.id}
                                                artwork={artwork}
                                                onClick={() => navigate(`/artworks/${artwork.id}`)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!loading && query && !isDiscovery && results.artists.length === 0 && results.artworks.length === 0 && (
                                <div className="no-results">
                                    <p>{t('no_results_found')}</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
